import { useState } from "react";
import { motion } from "framer-motion";
import { Ruler, AlertTriangle } from "lucide-react";
import ShutterMock from "./ShutterMock";

export default function SampleCard({ chapter, design, index }) {
  const [sel, setSel] = useState(design.def ?? 0);
  const colour = chapter.colours[sel];
  const cid = `${chapter.id}-${index}`;

  return (
    <motion.article
      data-testid={`card-${cid}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group"
    >
      <div className="relative aspect-[3/4] overflow-hidden border border-line bg-bone2">
        {design.photo ? (
          <img
            src={design.photo}
            alt={`${design.n} — ${chapter.title} shutter sample`}
            loading="lazy"
            className={`absolute inset-0 w-full h-full ${chapter.tall ? "object-contain p-5" : "object-cover"} transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]`}
          />
        ) : (
          <ShutterMock profile={design.p} color={colour.h} texture={chapter.texture} testid={`shutter-${cid}`} />
        )}
        <span className="absolute top-3 left-3 font-mono text-[9px] tracking-[0.25em] text-bone bg-ink/55 backdrop-blur-md px-2.5 py-1 rounded-full">
          {String(index + 1).padStart(2, "0")} / {String(chapter.designs.length).padStart(2, "0")}
        </span>
        <span className="absolute bottom-3 left-3 font-mono text-[9px] uppercase tracking-[0.25em] text-bone bg-ink/55 backdrop-blur-md px-2.5 py-1 rounded-full">
          {chapter.title}
        </span>
        {design.photo && (
          <img
            src="/images/watermark.png"
            alt=""
            aria-hidden="true"
            data-testid={`watermark-${cid}`}
            className="absolute opacity-80 pointer-events-none"
            style={
              design.watermark
                ? { left: `${design.watermark.x}%`, top: `${design.watermark.y}%`, width: `${design.watermark.w}%`, transform: "translateY(-50%)" }
                : { left: "50%", top: "50%", width: chapter.tall ? "46%" : "52%", transform: "translate(-50%, -50%)" }
            }
          />
        )}
      </div>

      <div className="bg-white border border-t-0 border-line p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-serif text-lg tracking-tight text-ink">{design.n}</h3>
          {design.tag && (
            <span data-testid={`tag-${cid}`} className="font-mono text-[8px] uppercase tracking-[0.2em] bg-ink text-bone px-2 py-1 rounded-sm">
              {design.tag}
            </span>
          )}
        </div>
        <p className={`font-mono text-[9px] uppercase tracking-[0.25em] mb-4 mt-1 ${design.sub ? "text-ink font-bold" : "text-beiged"}`}>
          {design.sub ?? `${chapter.title}${chapter.noShades ? "" : ` · ${chapter.colours.length} ${chapter.unit ?? "shades"}`}`}
        </p>

        {!chapter.noShades && (
          <>
            <div className="flex flex-wrap gap-1.5" data-testid={`swatches-${cid}`}>
          {chapter.colours.map((c, i) => (
            <button
              key={c.n}
              data-testid={`swatch-${cid}-${i}`}
              onClick={() => setSel(i)}
              aria-label={c.n}
              style={{ backgroundColor: c.h }}
              className={`w-5 h-5 rounded-full border border-ink/15 transition-all duration-300 ${
                sel === i ? "ring-2 ring-ink ring-offset-1 ring-offset-white scale-110" : "hover:scale-110"
              }`}
            />
          ))}
        </div>
            <p className="font-mono text-[10px] text-muted2 mt-2.5" data-testid={`shade-label-${cid}`}>
              {colour.n} · {colour.h}{design.photo ? "" : " · changes live"}
            </p>
          </>
        )}

        <ul className="space-y-1.5 border-t border-line pt-3.5 mt-4" data-testid={`notes-${cid}`}>
          {chapter.notes.slice(0, 2).map((note, i) => {
            const warn = /2400|bending|risk/i.test(note);
            const Icon = warn ? AlertTriangle : Ruler;
            return (
              <li key={i} className="flex items-start gap-2 text-[10.5px] leading-relaxed text-muted2">
                <Icon size={12} strokeWidth={1.5} className={`mt-0.5 shrink-0 ${warn ? "text-beiged" : "text-ink/35"}`} />
                {note}
              </li>
            );
          })}
        </ul>
      </div>
    </motion.article>
  );
}
